import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { beautyAddProfessionalApi, beautyUpdateProfessionalApi } from "../../api/organizerBeautyApi";
import AvisoSenasBloqueadas from "../components/AvisoSenasBloqueadas";
import { Dinero } from "../canchas/primerUso/clubPasos";
import { Campo } from "../reservas/primerUso/barPasos";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import AccesoZoco from "./AccesoZoco";
import { BarraAccionesContext } from "./barraAcciones";
import HorarioSemanal from "./HorarioSemanal";
import { faltaProfesional, formDeProfesional, horarioDelSalon, idDe, payloadProfesional } from "./profesionalPayload";
import "./turnos-beauty.css";
import "../events/EventForm.css";
import "../primerUso/primerUso.css";
import "../reservas/reservas-organizer.css";

const etiquetaDuracion = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h} h` : "", m ? `${m} min` : ""].filter(Boolean).join(" ");
};

// "Sin límite" + 1 h ... 8 h in 30-minute steps.
const OPCIONES_DURACION_MAXIMA = [
  { value: "", label: "Sin límite" },
  ...Array.from({ length: 15 }, (_, i) => 60 + i * 30).map((min) => ({ value: String(min), label: etiquetaDuracion(min) })),
];

const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

/**
 * Detail of a professional (right side of the master/detail). `profesional` null = new.
 * `onSaved(msg, id)` reloads the list; `onVinculo(msg)` only reloads it; `onCancelar` closes a new one.
 * `onEliminar` triggers the delete confirmation from the top header;
 * `onCopiarLink` (owner, saved professional with a slug) copies the personal booking link.
 */
const ProfesionalForm = ({
  profesional,
  servicios = [],
  salon,
  senasBloqueadas,
  onCancelar,
  onSaved,
  onVinculo,
  onEliminar,
  onCopiarLink,
}) => {
  const [f, setF] = useState(() => formDeProfesional(profesional));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  // Track which services have the "Personalizar" toggle active
  const [personalizados, setPersonalizados] = useState(() => {
    const s = new Set();
    Object.entries(f.servicios || {}).forEach(([id, ov]) => {
      if (ov && (ov.precio || ov.duracion || ov.sena)) s.add(id);
    });
    return s;
  });

  useEffect(() => {
    setF(formDeProfesional(profesional));
    const s = new Set();
    (profesional?.serviceOverrides || []).forEach((ov) => {
      if (ov.price != null || ov.durationMinutes != null || ov.deposit != null) {
        s.add(idDe(ov.serviceId));
      }
    });
    setPersonalizados(s);
  }, [profesional]);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const alternar = (id) => {
    const { [id]: previo, ...resto } = f.servicios;
    if (previo) {
      setPersonalizados((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      set({ servicios: resto });
    } else {
      set({ servicios: { ...f.servicios, [id]: { precio: "", duracion: "", sena: "" } } });
    }
  };

  const alternarPersonalizacion = (id, activar) => {
    setPersonalizados((prev) => {
      const next = new Set(prev);
      if (activar) next.add(id);
      else next.delete(id);
      return next;
    });
    if (!activar) {
      setOverride(id, { precio: "", duracion: "", sena: "" });
    }
  };

  const setOverride = (id, patch) =>
    set({ servicios: { ...f.servicios, [id]: { ...f.servicios[id], ...patch } } });

  const copiarSalon = () => {
    if (!salon?.openingHours?.length) {
      setAviso("El salón todavía no tiene un horario cargado.");
      return;
    }
    setAviso("");
    set({ horario: horarioDelSalon(salon) });
  };

  const guardar = async () => {
    const falta = faltaProfesional(f, servicios);
    if (falta) {
      setError(falta);
      return;
    }
    const body = payloadProfesional(f);
    setError("");
    setSaving(true);
    try {
      let creadoId;
      if (profesional) await beautyUpdateProfessionalApi({ id: profesional._id, ...body });
      else creadoId = (await beautyAddProfessionalApi(body))?.data?.info?._id;
      onSaved(profesional ? "Profesional actualizado" : "Profesional creado", creadoId || profesional?._id);
    } catch (e) {
      setError(e?.response?.data?.message || "No pudimos guardar al profesional. Probá de nuevo.");
      setSaving(false);
    }
  };

  const titulo = profesional ? "Editar profesional" : "Nuevo profesional";
  const inicial = (f.nombre || "P").trim().charAt(0).toUpperCase() || "P";
  const idsSeleccionados = Object.keys(f.servicios);
  const numServiciosSeleccionados = idsSeleccionados.length;

  // Inside the salon editor the actions live in its sticky bar, next to "Volver".
  const barra = useContext(BarraAccionesContext);
  const acciones = (
    <>
      {!profesional && onCancelar && (
        <button type="button" className="zr-btn zr-btn--ghost" onClick={onCancelar} disabled={saving}>
          Cancelar
        </button>
      )}
      <button type="button" className="zr-btn zr-btn--dark" onClick={guardar} disabled={saving}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </button>
    </>
  );

  return (
    <section className="zr-pro-detail-card" aria-label={titulo}>
      <div className="zr-pro-detail-header">
        <div className="zr-pro-detail-hero">
          <span className="zr-avatar zr-avatar--lg">{inicial}</span>
          <div>
            <h3 className="zr-pro-detail-name">{f.nombre.trim() || "Nuevo profesional"}</h3>
            <p className="zr-pro-detail-sub">
              {profesional
                ? [f.especialidad.trim(), `${numServiciosSeleccionados} ${numServiciosSeleccionados === 1 ? "servicio" : "servicios"}`]
                    .filter(Boolean)
                    .join(" · ")
                : "Nuevo integrante del equipo"}
            </p>
          </div>
        </div>
        <div className="bt-pro-head-actions" style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", margin: 0 }}>
            <input
              type="checkbox"
              className="zr-switch"
              aria-label="Profesional activo"
              checked={f.activo}
              onChange={(e) => set({ activo: e.target.checked })}
            />
            <span>Profesional activo</span>
          </label>
          {profesional && onCopiarLink && (
            <button type="button" className="zr-btn zr-btn--ghost" onClick={onCopiarLink}>
              Copiar link
            </button>
          )}
          {profesional && onEliminar && (
            <button
              type="button"
              className="zr-btn zr-btn--ghost zr-side-actions__danger"
              aria-label={`Eliminar ${profesional.name}`}
              onClick={onEliminar}
              style={{
                color: "#e53935",
                border: "1px solid #fecaca",
                padding: "6px 14px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Eliminar
            </button>
          )}
        </div>
      </div>

      <div className="pu-body">
        {error && <div className="ef-alert" role="alert">{error}</div>}

        <Campo label="Nombre">
          <input
            className="ef-input"
            aria-label="Nombre del profesional"
            value={f.nombre}
            onChange={(e) => set({ nombre: e.target.value })}
            placeholder="Ej: Lucía Pérez"
          />
        </Campo>

        <Campo label="Especialidad (opcional)">
          <input
            className="ef-input"
            aria-label="Especialidad del profesional"
            value={f.especialidad}
            maxLength={60}
            onChange={(e) => set({ especialidad: e.target.value })}
            placeholder="Ej: Estilista y colorista"
          />
        </Campo>

        <Campo label="Duración máxima del turno" hint="Limita cuánto puede durar un turno con varios servicios.">
          <OrganizerPillSelect
            value={f.duracionMaxima}
            onChange={(val) => set({ duracionMaxima: val })}
            options={
              // A value saved outside the preset steps stays selectable instead of silently resetting.
              f.duracionMaxima && !OPCIONES_DURACION_MAXIMA.some((o) => o.value === f.duracionMaxima)
                ? [...OPCIONES_DURACION_MAXIMA, { value: f.duracionMaxima, label: etiquetaDuracion(Number(f.duracionMaxima)) }]
                : OPCIONES_DURACION_MAXIMA
            }
          />
        </Campo>

        <Campo
          label="Servicios que hace"
          hint="Si dejás vacío el precio, la duración o la seña propios, se usan los del servicio."
        >
          {servicios.length === 0 ? (
            <p className="ef-hint">Todavía no cargaste servicios. Cargalos primero en la pestaña Servicios.</p>
          ) : (
            <div className="pu-body zr-pro-servicios">
              {senasBloqueadas && <AvisoSenasBloqueadas />}
              {servicios.map((s) => {
                const id = idDe(s);
                const o = f.servicios[id];
                const estaAsignado = Boolean(o);
                const estaPersonalizado = personalizados.has(id);

                return (
                  <div
                    key={id}
                    className="pu-summary"
                    style={{
                      border: estaAsignado ? "1px solid #cbd5e1" : "1px solid #f1f5f9",
                      background: estaAsignado ? "#fff" : "#f8fafc",
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <label className="bt-srv-pick" style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer", margin: 0 }}>
                        <input
                          type="checkbox"
                          className="zr-switch"
                          aria-label={s.name}
                          checked={estaAsignado}
                          onChange={() => alternar(id)}
                        />
                        <span style={{ fontWeight: 600 }}>{s.name}</span>
                        <small style={{ color: "#64748b" }}>
                          {plata(s.price)} · {s.durationMinutes} min
                        </small>
                      </label>

                      {estaAsignado && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
                          <input
                            type="checkbox"
                            className="zr-switch"
                            aria-label={`Personalizar ${s.name}`}
                            checked={estaPersonalizado}
                            onChange={(e) => alternarPersonalizacion(id, e.target.checked)}
                          />
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: estaPersonalizado ? "var(--zr-ink)" : "#94a3b8",
                            }}
                          >
                            Personalizar
                          </span>
                        </label>
                      )}
                    </div>

                    {estaAsignado && estaPersonalizado && (
                      <div
                        className="pu-grid-2"
                        style={{
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: "1px dashed #e2e8f0",
                        }}
                      >
                        <Campo label={`Precio propio de ${s.name}`}>
                          <Dinero
                            label={`Precio propio de ${s.name}`}
                            value={o.precio}
                            placeholder={String(s.price ?? 0)}
                            onChange={(v) => setOverride(id, { precio: v })}
                          />
                        </Campo>
                        <Campo label={`Duración propia de ${s.name}`}>
                          <input
                            className="ef-input"
                            type="number"
                            min="5"
                            step="5"
                            aria-label={`Duración propia de ${s.name}`}
                            value={o.duracion}
                            placeholder={`${s.durationMinutes} min`}
                            onChange={(e) => setOverride(id, { duracion: e.target.value })}
                          />
                        </Campo>
                        <Campo label={`Seña propia de ${s.name}`}>
                          <Dinero
                            label={`Seña propia de ${s.name}`}
                            value={o.sena}
                            disabled={senasBloqueadas}
                            placeholder={s.deposit ? String(s.deposit) : "Sin seña"}
                            onChange={(v) => setOverride(id, { sena: v })}
                          />
                        </Campo>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Campo>

        <Campo label="Horario semanal" hint="Podés cargar más de un tramo por día, por ejemplo mañana y tarde.">
          {aviso && <p className="ef-hint">{aviso}</p>}
          <HorarioSemanal horario={f.horario} onChange={(horario) => set({ horario })} onCopiarSalon={copiarSalon} />
        </Campo>

        {profesional && <AccesoZoco profesional={profesional} salon={salon} onCambio={onVinculo} />}
      </div>

      {barra ? createPortal(acciones, barra) : <div className="zr-side-panel__foot">{acciones}</div>}
    </section>
  );
};

export default ProfesionalForm;
